let RUNTIME_API_BASE = null;
const getApiBase = async () => {
  if (RUNTIME_API_BASE) return RUNTIME_API_BASE;
  try {
    const res = await fetch('/config.json', { cache: 'no-store' });
    if (res.ok) {
      const cfg = await res.json();
      if (cfg && typeof cfg.API_BASE === 'string' && cfg.API_BASE.length > 0) {
        RUNTIME_API_BASE = cfg.API_BASE;
        return RUNTIME_API_BASE;
      }
    }
  } catch {}
  RUNTIME_API_BASE = '/api/v1';
  return RUNTIME_API_BASE;
};

class PublicApiClient {
  constructor() {
    this.baseURL = '/api/v1';
    this.cache = new Map();
  }

  // Simple request method without auth handling
  async request(url, options = {}) {
    const base = await getApiBase();
    const fullUrl = url.startsWith('http') ? url : `${base}${url}`;
    
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    console.log(`[Public API Request] Making request to: ${fullUrl}`, { options, method: config.method });

    try {
      const response = await fetch(fullUrl, config);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        // Handle specific error cases without redirecting
        if (response.status === 400 && errorData.message && errorData.message.includes('Invalid ID format')) {
          throw new Error('Invalid ID format - the provided ID does not match the expected pattern');
        } else if (response.status === 404) {
          throw new Error('Resource not found');
        } else if (response.status === 401) {
          throw new Error('Unauthorized - this endpoint requires authentication');
        } else if (response.status === 403) {
          throw new Error('Forbidden - you do not have permission to perform this action');
        } else {
          throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
        }
      }

      // Handle 204 No Content responses
      if (response.status === 204) {
        return null;
      }

      const result = await response.json();
      console.log(`[Public API Response] Received response for: ${fullUrl}`, { 
        resultCount: Array.isArray(result) ? result.length : 'single item' 
      });
      
      return result;
    } catch (error) {
      console.error('Public API request failed:', error);
      
      // Создаем более информативную ошибку
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        const connectionError = new Error('Could not connect to the server');
        connectionError.name = 'ConnectionError';
        connectionError.originalError = error;
        throw connectionError;
      }
      
      throw error;
    }
  }

  // GET request with caching
  async get(endpoint, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;
    const cacheKey = this.getCacheKey(endpoint, params);
    
    // Check cache first
    const cached = this.getCachedResponse(cacheKey);
    if (cached) {
      console.log(`[Public API Cache] Using cached response for: ${endpoint}`, { params });
      return cached;
    }
    
    console.log(`[Public API Request] Making GET request to: ${endpoint}`, { params });
    
    // Make request and cache result
    const result = await this.request(url, { method: 'GET' });
    this.setCachedResponse(cacheKey, result);
    
    return result;
  }

  // POST request (for public endpoints that might need it)
  async post(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // GET request without caching (for real-time data like payment status)
  async getNoCache(endpoint, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;
    
    console.log(`[Public API Request] Making GET request (no cache) to: ${endpoint}`, { params });
    
    return this.request(url, { method: 'GET' });
  }

  // Cache management
  getCacheKey(endpoint, params) {
    return `${endpoint}?${new URLSearchParams(params).toString()}`;
  }

  getCachedResponse(cacheKey) {
    const cached = this.cache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < 5 * 60 * 1000) { // 5 minutes cache
      return cached.data;
    }
    return null;
  }

  setCachedResponse(cacheKey, data) {
    this.cache.set(cacheKey, {
      data,
      timestamp: Date.now()
    });
  }

  clearCache() {
    this.cache.clear();
  }
}

// Create singleton instance
export const publicApiClient = new PublicApiClient();

// Get language and footer settings
export const getLanguageSettings = async () => {
  try {
    const base = await getApiBase();
    const response = await fetch(`${base}/public/language-settings`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching language settings:', error);
    // Return default values if fetch fails
    return {
      language: 'ru',
      footer_copyright: '© 2024 BookForge Publishing. Все права защищены.',
      footer_text: 'Создаем и издаем качественную литературу для думающих читателей'
    };
  }
};
